import useSWR, { mutate as globalMutate, type SWRConfiguration } from "swr";
import { type AxiosResponse } from "axios";

import { api } from "../modules/api_client";
import type { PaginatedResponse } from "../modules/pagination/data/types/pagination";

type ApiServiceMethod<T> = () => Promise<AxiosResponse<T>>;

export function useApi<T>(
  key: string | null,
  serviceMethod: ApiServiceMethod<T>,
  options?: SWRConfiguration
) {
  const fetcher = async () => {
    const response = await serviceMethod();
    return response.data;
  };

  return useSWR<T>(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    ...options,
  });
}

type PaginatedApiServiceMethod<T, TQuery> = (
  query: TQuery
) => Promise<AxiosResponse<PaginatedResponse<T>>>;

type UsePaginatedApiProps<T, TQuery> = {
  key: string | null;
  serviceMethod: PaginatedApiServiceMethod<T, TQuery>;
  query?: TQuery;
  options?: SWRConfiguration;
};

export function usePaginatedApi<T, TQuery>({
  key,
  serviceMethod,
  query = {} as TQuery,
  options,
}: UsePaginatedApiProps<T, TQuery>) {
  const fetcher = async () => {
    const response = await serviceMethod(query);
    return response.data as PaginatedResponse<T>;
  };

  // Create unique cache key with params
  const cacheKey = key ? `${key}-${JSON.stringify(query)}` : null;

  const swrResult = useSWR<PaginatedResponse<T>>(cacheKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    ...options,
  });

  // Return with isLoading computed from SWR's isLoading and isValidating
  return {
    ...swrResult,
    isLoading:
      swrResult.isLoading ?? (!swrResult.data && swrResult.isValidating),
  };
}

type UseDetailApiProps<T> = {
  key: string | null;
  serviceMethod: ApiServiceMethod<T>;
  options?: SWRConfiguration;
};

export function useDetailApi<T>({
  key,
  serviceMethod,
  options,
}: UseDetailApiProps<T>) {
  const fetcher = async () => {
    const response = await serviceMethod();
    return response.data;
  };

  return useSWR<T>(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    ...options,
  });
}

export const fetcher = async (url: string) => {
  if (!url) return null;

  const response = await api.get(url);
  const { data } = response;

  if (data.meta || data.links) {
    return { data: data.data, meta: data.meta, links: data.links };
  }

  return data;
};

/**
 * Revalidate every cache entry whose key starts with `prefix`.
 *
 * `usePaginatedApi` composes its key as `${key}-${JSON.stringify(query)}`, so a
 * bare `mutate("admin-warranty-claims")` matches no entry and the list is left
 * stale. This matches the base key and every paginated/filtered variant of it,
 * and also covers plain `useApi` keys (stats endpoints) that equal the prefix.
 */
export function mutateByPrefix(prefix: string) {
  return globalMutate(
    (key) => typeof key === "string" && (key === prefix || key.startsWith(`${prefix}-`)),
    undefined,
    { revalidate: true },
  );
}

/**
 * Re-type a generated client response.
 *
 * Controllers that rely on `Serialize`/`SerializePaginated` without an explicit
 * `@ApiResponse({ type })` generate as `AxiosPromise<void>`, so hooks have to
 * assert the real shape. Doing it here keeps that assertion in one place
 * instead of an `as any` in every hook.
 */
export function asPaginatedResponse<T>(
  p: Promise<AxiosResponse<unknown>>,
): Promise<AxiosResponse<PaginatedResponse<T>>> {
  return p as unknown as Promise<AxiosResponse<PaginatedResponse<T>>>;
}

export function asDetailResponse<T>(
  p: Promise<AxiosResponse<unknown>>,
): Promise<AxiosResponse<T>> {
  return p as unknown as Promise<AxiosResponse<T>>;
}
